// Canonical money formatter for the app. Promoted from the per-feature copies
// (my-pay, payroll) so currency rendering is consistent everywhere. Handles unknown
// currency codes gracefully and supports a compact form for stat cards/axes.
//
// NOTE: payroll/lib/format.ts and my-pay/lib/format.ts still carry local copies;
// converge them onto this module in a follow-up sweep (see [[reusable-components-pref]]).

// Display standard: ISO 4217 CODE + en-US grouped digits — "TZS 1,500,000",
// compact "TZS 1.5M". Locale symbols ("TSh", "KSh", …) are deliberately NOT
// used: they rendered inconsistently against the ISO codes shown elsewhere
// (payslip headers, exports, API payloads), and codes stay unambiguous once
// multi-currency lands.

// East-African currencies are effectively whole-unit; USD keeps 2 dp.
const ZERO_DP = new Set(['TZS', 'KES', 'RWF', 'UGX']);

// Fraction digits for a currency — the single source shared by formatMoney and
// the MoneyInput component so on-screen editing and display never disagree.
export function fractionDigitsFor(currency = 'TZS'): number {
  return ZERO_DP.has(currency) ? 0 : 2;
}

export function formatMoney(amount: number, currency = 'TZS', opts?: { compact?: boolean }): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const code = currency || 'TZS';
  const dp = fractionDigitsFor(code);
  const digits = opts?.compact
    ? new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
    : n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return `${code} ${digits}`;
}
